import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { HerfindahlIndexStrings } from "@/components/tools/herfindahl-index-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  band,
  concentrationRatio,
  equivalentFirms,
  hhi,
  type Merger,
} from "@/lib/tool-kit/econ-micro/concentration";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

export interface Firm {
  name: string;
  /** Percentage points. */
  share: number;
}

export function ConcentrationView({
  firms,
  strings: s,
  fmt,
}: {
  firms: Firm[];
  strings: HerfindahlIndexStrings;
  fmt: Fmt;
}) {
  const shares = firms.map((f) => f.share);
  const index = hhi(shares);
  const n = (v: number, digits = 2) =>
    fmt(v, { maximumFractionDigits: digits });
  const equivalent = equivalentFirms(index);
  const ordered = [...firms].sort((a, b) => b.share - a.share);
  const rows = ordered.map((f) => [
    f.name,
    n(f.share),
    n(f.share * f.share),
    fmt(index > 0 ? (f.share * f.share) / index : 0, {
      style: "percent",
      maximumFractionDigits: 1,
    }),
  ]);
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard emphasis label={s.statHhi} value={n(index)} />
        <StatCard
          label={s.statBand}
          value={band(index) === "highly" ? s.bandHighly : s.bandNotHighly}
        />
        <StatCard
          label={s.statEquivalent}
          value={equivalent === null ? "—" : n(equivalent)}
        />
        <StatCard
          label={s.statCr1}
          value={`${n(concentrationRatio(shares, 1))}%`}
        />
        <StatCard
          label={s.statCr4}
          value={`${n(concentrationRatio(shares, 4))}%`}
        />
        <StatCard label={s.statFirms} value={n(firms.length, 0)} />
      </StatGrid>
      <p className="text-muted-foreground text-sm">{s.bandNote}</p>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.tableHeading}</h3>
        <DataTable
          head={[s.columnFirm, s.columnShare, s.columnSquare, s.columnPart]}
          rows={rows}
        />
        <p className="text-muted-foreground text-xs">
          {t(s.workSum, { index: n(index) })}
        </p>
      </div>
    </div>
  );
}

export function MergerView({
  first,
  second,
  result,
  strings: s,
  fmt,
}: {
  first: Firm;
  second: Firm;
  result: Merger;
  strings: HerfindahlIndexStrings;
  fmt: Fmt;
}) {
  const n = (v: number) => fmt(v, { maximumFractionDigits: 2 });
  const facts = {
    delta: n(result.increase),
    after: n(result.after),
    share: n(result.mergedShare),
  };
  const reasons: string[] = [];
  if (result.highlyConcentratedProng) {
    reasons.push(t(s.reasonMarket, facts));
  }
  if (result.shareProng) {
    reasons.push(t(s.reasonShare, facts));
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.mergerResultHeading}</h3>
      <ul className="space-y-1 font-mono text-foreground text-sm">
        <li>
          {t(s.workIncrease, {
            a: n(first.share),
            b: n(second.share),
            delta: facts.delta,
          })}
        </li>
        <li>
          {t(s.workAfter, {
            before: n(result.before),
            delta: facts.delta,
            after: facts.after,
          })}
        </li>
        <li>
          {t(s.workMergedShare, {
            ...facts,
            a: n(first.share),
            b: n(second.share),
          })}
        </li>
      </ul>
      {result.presumed ? (
        <div className="space-y-1">
          <p className="font-medium text-foreground text-sm">{s.presumedYes}</p>
          {reasons.map((reason) => (
            <p className="text-foreground text-sm" key={reason}>
              {reason}
            </p>
          ))}
        </div>
      ) : (
        <p className="text-foreground text-sm">{t(s.presumedNo, facts)}</p>
      )}
      <p className="text-muted-foreground text-xs">{s.mergerCaveat}</p>
    </div>
  );
}
